import React, { useState } from "react";
import Header from "components/Header";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    PointElement,
    LineElement,
    Filler
} from "chart.js";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

/* KPI DATA */

const kpiData = [
    {
        title: "Maternal Mortality Ratio",
        value: "118",
        change: "-9% vs Last Year",
        trend: [140, 135, 192, 128, 122, 178]
    },
    {
        title: "Infant Mortality Rate",
        value: "24",
        change: "-3 vs Last Year",
        trend: [30, 29, 22, 26, 25, 29]
    },
    {
        title: "Institutional Deliveries",
        value: "88%",
        change: "+2% vs Last Month",
        trend: [80, 82, 89, 85, 86, 88]
    },
    {
        title: "Full Immunization",
        value: "91%",
        change: "+4% vs Last Month",
        trend: [75, 78, 82, 89, 89, 91]
    },
    {
        title: "NCD Screening",
        value: "62%",
        change: "+7% vs Last Month",
        trend: [40, 45, 68, 59, 58, 62]
    },
    {
        title: "Bed Occupancy",
        value: "83%",
        change: "+5% vs Last Week",
        trend: [70, 72, 79, 78, 80, 83]
    }
];

/* IMMUNIZATION CHART */

function ImmunizationTrendChart() {

    const labels = [
        "Apr 2025", "May 2025", "Jun 2025",
        "Jul 2025", "Aug 2025", "Sep 2025", "Oct 2025", "Nov 2025", "Dec 2025", "Jan 2026", "Feb 2026", "Mar 2026"
    ];

    const vaccinated = [
        18200, 19500, 20000, 23800, 25500, 26400,
        29200, 30500, 31800, 33200, 34900, 31200
    ];

    const coverage = [
        78, 80, 82, 85, 87, 89, 90, 91, 92, 93, 94, 95
    ];

    const measles = [
        70, 72, 74, 76, 78, 80, 82, 83, 84, 85, 86, 88
    ];

    const birthDose = [
        60, 62, 64, 67, 70, 73, 75, 77, 79, 81, 83, 85
    ];

    const pentavalent = [
        65, 68, 70, 73, 75, 78, 80, 82, 84, 86, 88, 90
    ];

    const chartData = {
        labels,
        datasets: [

            {
                type: "bar",
                label: "Children Vaccinated",
                data: vaccinated,
                tension: 0.2,
                backgroundColor: "rgba(94,114,228,0.4)",
                borderRadius: 6,
                yAxisID: "y"
            },
            {
                label: "Full Immunization %",
                data: coverage,
                borderColor: "#2dce89",
                tension: 0.4,
                borderWidth: 3,
                pointRadius: 3,
                yAxisID: "y1"
            },

            {
                label: "Measles Coverage %",
                data: measles,
                borderColor: "#11cdef",
                tension: 0.4,
                pointRadius: 2,
                yAxisID: "y1"
            },

            {
                label: "Birth Dose %",
                data: birthDose,
                borderColor: "#fb6340",
                tension: 0.4,
                pointRadius: 2,
                yAxisID: "y1"
            },

            {
                label: "Pentavalent Coverage %",
                data: pentavalent,
                borderColor: "#ffd600",
                tension: 0.4,
                pointRadius: 2,
                yAxisID: "y1"
            },

            {
                label: "Target 95%",
                data: new Array(labels.length).fill(95),
                borderColor: "#f5365c",
                borderDash: [6, 6],
                pointRadius: 0,
                borderWidth: 2,
                yAxisID: "y1"
            }

        ]
    };

    const options = {

        responsive: true,
        maintainAspectRatio: false,

        plugins: {

            legend: { position: "top" },

            tooltip: {
                callbacks: {
                    afterBody: function (context) {

                        const i = context[0].dataIndex;

                        const facilities = [
                            "420 PHC active",
                            "430 PHC active",
                            "450 PHC active",
                            "470 PHC active",
                            "490 PHC active",
                            "520 PHC active",
                            "545 PHC active",
                            "560 PHC active",
                            "575 PHC active",
                            "590 PHC active",
                            "610 PHC active",
                            "635 PHC active"
                        ];

                        return `Facilities Active: ${facilities[i]}`;
                    }
                }
            },
            datalabels: false,

        },

        scales: {

            y: {
                title: { display: true, text: "Children Vaccinated" },
                grid: { color: "#f1f1f1" }
            },

            y1: {
                position: "right",
                min: 55,
                max: 100,
                title: { display: true, text: "Coverage %" },
                grid: { drawOnChartArea: false }
            }

        }

    };

    return (

        <div className="bg-white rounded-xl shadow-md p-6">

            <h3 className="text-lg font-semibold mb-4">
                Immunization Performance Monitoring
            </h3>

            <div className="h-[420px]">
                <Line data={chartData} options={options} />
            </div>

            <p className="text-xs text-gray-500 mt-3">
                Campaign Highlight: Intensified Mission Indradhanush (Apr–May) significantly increased vaccination coverage across districts.
            </p>

        </div>

    );

}

/* SPARKLINE */

function SparkLine({ data }) {

    const chartData = {
        labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
        datasets: [{
            data: data,
            borderColor: "#5e72e4",
            borderWidth: 2,
            pointRadius: 0,
            tension: 0.4
        }]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false }, tooltip: { enabled: false } },
        scales: { x: { display: false }, y: { display: false } }
    };

    return (
        <div className="h-14 mt-2">
            <Line data={chartData} options={options} />
        </div>
    );

}

/* KPI CARD */

// function KPICard({ title, value, change, trend }) {

//     const isPositive = change.includes("+");

//     return (

//         <div className="bg-white shadow-md rounded-xl p-4">

//             <p className="text-xs font-semibold text-gray-500 uppercase">
//                 {title}
//             </p>

//             <h2 className="text-2xl font-bold text-gray-800 mt-1">
//                 {value}
//             </h2>

//             <p className={`text-xs mt-1 ${isPositive ? "text-green-500" : "text-red-500"}`}>
//                 {change}
//             </p>

//             <SparkLine data={trend} />

//         </div>

//     );

// }
function KPICard({ title, value, change, trend }) {

    const isPositive = change.includes("+");

    const data = {
        labels: trend.map((_, i) => i),
        datasets: [
            {
                data: trend,
                borderColor: isPositive ? "#2dce89" : "#f5365c",
                backgroundColor: isPositive
                    ? "rgba(45,206,137,0.15)"
                    : "rgba(245,54,92,0.15)",
                fill: true,
                tension: 0.4,
                pointRadius: 0,
                borderWidth: 2
            }
        ]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: { enabled: true },
            datalabels:false
        },
        scales: {
            x: { display: false },
            y: { display: false }
        }
    };

    return (

        <div className="bg-white shadow-md rounded-xl p-4 h-auto flex flex-col justify-between">

            <div>
                <p className="text-xs font-semibold text-gray-500 uppercase">
                    {title}
                </p>

                <h2 className="text-2xl font-bold text-gray-800 mt-1">
                    {value}
                </h2>

                <p className={`text-xs mt-1 ${isPositive ? "text-green-500" : "text-red-500"}`}>
                    {change}
                </p>
            </div>

            <div className="h-12">
                <Line data={data} options={options} />
            </div>

        </div>

    );

}

function MaternalCareContinuumChart() {

    const labels = [
        "Ahmednagar",
        "Akola",
        "Amravati",
        "Aurangabad",
        "Beed",
        "Bhandara",
        "Buldhana",
        "Chandrapur",
        "Dhule",
        "Gadchiroli",
        "Gondia",
        "Hingoli",
        "Jalgaon",
        "Jalna",
        "Kolhapur",
        "Latur",
        "Mumbai City",
        "Mumbai Suburban",
        "Nagpur",
        "Nanded",
        "Nandurbar",
        "Nashik",
        "Osmanabad",
        "Palghar",
        "Parbhani",
        "Pune",
        "Raigad",
        "Ratnagiri",
        "Sangli",
        "Satara",
        "Sindhudurg",
        "Solapur",
        "Thane",
        "Wardha",
        "Washim",
        "Yavatmal"
    ];
    const anc1 = [
        95, 94, 93, 92, 91, 90, 89, 90, 88, 87, 86, 85,
        90, 89, 91, 88, 93, 92, 94, 90, 87, 91, 89, 88,
        87, 95, 93, 86, 90, 91, 89, 92, 94, 88, 87, 86
    ];

    const anc2 = [
        91, 90, 89, 88, 87, 86, 85, 86, 84, 83, 82, 81,
        87, 86, 88, 84, 89, 88, 90, 86, 83, 87, 85, 84,
        83, 91, 89, 82, 86, 87, 85, 88, 90, 84, 83, 82
    ];

    const anc3 = [
        87, 86, 85, 84, 83, 82, 81, 82, 80, 79, 78, 77,
        83, 82, 84, 80, 85, 84, 86, 82, 79, 83, 81, 80,
        79, 87, 85, 78, 82, 83, 81, 84, 86, 80, 79, 78
    ];

    const anc4 = [
        84, 83, 82, 81, 80, 79, 78, 79, 77, 76, 75, 74,
        80, 79, 81, 77, 82, 81, 83, 79, 76, 80, 78, 77,
        76, 84, 82, 75, 79, 80, 78, 81, 83, 77, 76, 75
    ];

    const highRisk = [
        9, 10, 11, 10, 12, 11, 13, 12, 11, 14, 13, 12,
        10, 11, 9, 12, 10, 11, 9, 13, 14, 10, 11, 12,
        13, 9, 10, 14, 11, 10, 12, 9, 10, 13, 12, 14
    ];

    const targetLine = new Array(labels.length).fill(85);

    const data = {
        labels,
        datasets: [

            {
                type: "bar",
                label: "ANC1 Registration %",
                data: anc1,
                backgroundColor: "rgba(94,114,228,0.6)",
                borderRadius: 6
            },

            {
                type: "bar",
                label: "ANC2 %",
                data: anc2,
                backgroundColor: "rgba(45,206,137,0.6)",
                borderRadius: 6
            },

            {
                type: "bar",
                label: "ANC3 %",
                data: anc3,
                backgroundColor: "rgba(17,205,239,0.6)",
                borderRadius: 6
            },

            {
                type: "bar",
                label: "ANC4 Completion %",
                data: anc4,
                backgroundColor: "rgba(251,99,64,0.6)",
                borderRadius: 6
            },

            {
                type: "line",
                label: "High Risk Pregnancy %",
                data: highRisk,
                borderColor: "#f5365c",
                backgroundColor: "#f5365c",
                tension: 0.4,
                pointRadius: 4,
                yAxisID: "y1"
            },

            {
                type: "line",
                label: "ANC4 Target (85%)",
                data: targetLine,
                borderColor: "#2dce89",
                borderDash: [6, 6],
                pointRadius: 0,
                borderWidth: 2,
                yAxisID: "y"
            }

        ]
    };

    const options = {

        responsive: true,
        maintainAspectRatio: false,

        interaction: {
            mode: "index",
            intersect: false
        },

        plugins: {

            legend: {
                position: "top"
            },

            tooltip: {

                callbacks: {

                    footer: function (context) {

                        const district = context[0].label;
                        return "Action: Monitor ANC follow-up in " + district;

                    }

                }

            },
            datalabels: false

        },

        scales: {

            y: {
                beginAtZero: true,
                max: 100,
                title: {
                    display: true,
                    text: "ANC Coverage %"
                }
            },

            y1: {
                position: "right",
                beginAtZero: true,
                max: 20,
                grid: {
                    drawOnChartArea: false
                },
                title: {
                    display: true,
                    text: "High Risk %"
                }
            }

        }

    };

    return (

        <div className="bg-white rounded-xl shadow-md p-6">

            <h3 className="text-lg font-semibold mb-4">
                Maternal Care Continuum (ANC1 → ANC4 + High Risk Detection)
            </h3>

            <div className="h-96">
                <Bar data={data} options={options} />
            </div>

        </div>

    );

}
/* MAIN DASHBOARD */
function MaternalChildFunnel() {

    const labels = [
        "Estimated Pregnant Women",
        "ANC Registered",
        "ANC4 Completed",
        "Institutional Deliveries",
        "Live Births",
        "Survived (No Neonatal Death)"
    ];

    const values = [100000, 92000, 78000, 85000, 82000, 79000];

    const data = {
        labels,
        datasets: [
            {
                label: "Beneficiaries",
                data: values,
                backgroundColor: [
                    "#5e72e4",
                    "#2dce89",
                    "#11cdef",
                    "#fb6340",
                    "#f5365c",
                    "#8898aa"
                ],
                borderRadius: 8,
                barThickness: 30
            }
        ]
    };

    const options = {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,

        plugins: {

            legend: {
                display: false
            },

            tooltip: {
                callbacks: {
                    label: function (context) {
                        return context.raw.toLocaleString() + " beneficiaries";
                    },
                    afterLabel: function (context) {

                        const current = context.raw;
                        const prev = values[context.dataIndex - 1];

                        if (!prev) return "";

                        const drop = (((prev - current) / prev) * 100).toFixed(1);

                        return `Drop-off: ${drop}%`;

                    }
                }
            },
            datalabels: false

        },

        scales: {

            x: {
                title: {
                    display: true,
                    text: "Number of Beneficiaries"
                }
            },

            y: {
                title: {
                    display: true,
                    text: "Stages"
                }
            }

        }

    };

    return (

        <div className="bg-white rounded-xl shadow-md p-6 mt-6">

            <h3 className="text-lg font-semibold mb-4">
                Maternal Health & Child Care 
            </h3>

            <div className="h-96">
                <Bar data={data} options={options} />
            </div>

        </div>

    );
}
function MaternalDeathAnalysis() {

    const data = {
        labels: [
            "Hemorrhage",
            "Hypertension",
            "Sepsis",
            "Unsafe Abortion",
            "Anemia",
            "Others"
        ],
        datasets: [
            {
                data: [32, 24, 18, 10, 9, 7],
                backgroundColor: [
                    "#BFC7F4",
                    "#fb6340",
                    "#ffd600",
                    "#11cdef",
                    "#2dce89",
                    "#8898aa"
                ],
                borderWidth: 1
            }
        ]
    };

    const options = {

        responsive: true,
        maintainAspectRatio: false,

        plugins: {

            legend: {
                position: "right"
            },

            tooltip: {
                callbacks: {
                    label: function(context) {
                        return `${context.label}: ${context.raw}%`;
                    }
                }
            },
            datalabels:false

        }

    };

    return (

        <div className="bg-white rounded-xl shadow-md p-8 mt-6">

            <h3 className="text-lg font-semibold mb-4">
                Maternal Death Analysis (Cause-wise)
            </h3>

            <div className="flex items-center">

                <div className="w-1/2 h-80 m-4">
                    <Doughnut data={data} options={options} />
                </div>

                <div className="w-1/2 pl-6">

                    <h4 className="text-md font-semibold text-red-600 mb-2">
                         Key Insight
                    </h4>

                    <p className="text-sm text-gray-700">
                        Hemorrhage accounts for the highest maternal deaths (32%).
                        Immediate focus required on blood availability, emergency
                        transport, and facility readiness.
                    </p>

                    <div className="mt-4 text-sm text-gray-600">
                        <p>• Hypertension indicates need for BP monitoring</p>
                        <p>• Sepsis suggests infection control gaps</p>
                        <p>• Anemia highlights nutrition challenges</p>
                    </div>

                </div>

            </div>

        </div>

    );

}
export default function OverviewDashboard() {

    const handleMenuClick = () => { };

    return (

        <div className="min-h-screen bg-gray-50">

            <Header
                onMenuClick={handleMenuClick}
                dashboardName="Health Dashboard"
            />

            <div className="px-6 py-6">

                <div className="grid grid-cols-6 gap-2">

                    {kpiData.map((kpi, index) => (
                        <KPICard key={index} {...kpi} />
                    ))}

                </div>

            </div>

            <div className="px-6 pb-8">

                <div className="grid grid-cols-12 gap-4">

                    <div className="col-span-12">
                        <ImmunizationTrendChart />
                    </div>
                    <div className="col-span-12">
                        <MaternalCareContinuumChart />
                    </div>
                    <div className="col-span-6">
                        <MaternalChildFunnel />
                    </div>
                    <div className="col-span-6">
                        <MaternalDeathAnalysis />
                    </div>
                    

                </div>

            </div>

        </div>

    );

}